import { useRef, useState } from 'react';
import { prepareImage, type AgentImage } from '../../api/agent';
import { Button, Icon } from '../../ui';

const MAX_IMAGES = 6;

/**
 * Words plus up to six pictures — the whole input to a build or a change. A screenshot of an
 * app, game or site is the most precise way to say "like this", so attaching is one tap.
 */
export default function Composer({
  placeholder,
  submitLabel,
  busy,
  onSubmit,
  autoFocus,
  examples,
  initialText,
  onTextChange,
}: {
  placeholder: string;
  submitLabel: string;
  busy: boolean;
  onSubmit: (text: string, images: AgentImage[]) => void;
  autoFocus?: boolean;
  examples?: string[];
  /** What they had already typed in the other builder (the ⚡ switch carries it over). */
  initialText?: string;
  onTextChange?: (text: string) => void;
}) {
  const [text, setTextState] = useState(initialText ?? '');
  const setText = (t: string) => {
    setTextState(t);
    onTextChange?.(t);
  };
  const [images, setImages] = useState<Array<AgentImage & { preview: string }>>([]);
  const [reading, setReading] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  async function addFiles(files: FileList | null) {
    if (!files?.length) return;
    setProblem(null);
    setReading(true);
    try {
      const room = MAX_IMAGES - images.length;
      const picked = [...files].slice(0, room);
      const ready = await Promise.all(picked.map(async (f) => ({ ...(await prepareImage(f)), preview: URL.createObjectURL(f) })));
      setImages((prev) => [...prev, ...ready]);
      if (files.length > room) setProblem(`Up to ${MAX_IMAGES} pictures.`);
    } catch {
      setProblem("One of those pictures couldn't be read. Try a screenshot or a JPEG.");
    } finally {
      setReading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  const canSend = text.trim().length > 0 && !busy && !reading;

  return (
    <form
      className="studio-composer clay-card"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSend) onSubmit(text.trim(), images.map(({ preview: _p, ...img }) => img));
      }}
    >
      <label className="visually-hidden" htmlFor="studio-prompt">{placeholder}</label>
      <textarea
        id="studio-prompt"
        className="studio-textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        rows={4}
        maxLength={4000}
        autoFocus={autoFocus}
      />
      {images.length > 0 && (
        <ul className="studio-thumbs" aria-label="Attached pictures">
          {images.map((img, i) => (
            <li key={img.preview} className="studio-thumb">
              <img src={img.preview} alt={`Attached picture ${i + 1}`} />
              <button
                type="button"
                className="studio-thumb-remove pressable"
                aria-label={`Remove picture ${i + 1}`}
                onClick={() => setImages((prev) => prev.filter((_, j) => j !== i))}
              >
                <Icon name="close" size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {problem && <p className="studio-problem" role="alert">{problem}</p>}
      <div className="studio-composer-row">
        <button
          type="button"
          className="studio-attach pressable"
          onClick={() => fileInput.current?.click()}
          disabled={images.length >= MAX_IMAGES || reading}
          aria-label="Attach a screenshot or picture"
        >
          <Icon name="assets" size={20} />
          <span>{reading ? 'Reading…' : 'Add a screenshot'}</span>
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => void addFiles(e.target.files)}
        />
        <Button title={submitLabel} type="submit" loading={busy} disabled={!canSend} className="studio-send" />
      </div>
      {examples && examples.length > 0 && !text && (
        <div className="studio-examples" aria-label="Ideas">
          {examples.map((ex) => (
            <button key={ex} type="button" className="studio-example pressable" onClick={() => setText(ex)}>
              {ex}
            </button>
          ))}
        </div>
      )}
    </form>
  );
}
